import { useEffect, useState } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Empty, ErrorBox, Kbd, Loading, Modal, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { MerchantPicker } from './pickers.tsx';
import type { LinkRow, MerchantLinks, Named, RegeneratePreview } from './types.ts';

const CHANGE_LABELS = { new: 'new', changed: 'changed', same: 'unchanged', fails: 'fails' } as const;

/** Coupon and merchant links of one merchant: which network is active per site, manual activation, and regeneration. */
export function Links() {
  const { query, setQuery } = useRouter();
  const toast = useToast();
  const changes = useChanges();
  const merchantRef = query.get('merchant') ?? '';
  const siteRef = query.get('site') ?? '';
  const data = useOp<MerchantLinks>('admin.links.list', { merchantRef, ...(siteRef ? { siteRef } : {}) }, changes, Boolean(merchantRef));
  const [merchantName, setMerchantName] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [programRef, setProgramRef] = useState('');
  const [preview, setPreview] = useState<{ input: Record<string, unknown>; result: RegeneratePreview } | null>(null);
  const [busy, setBusy] = useState(false);
  const coupons = data.data?.coupons ?? [];

  useEffect(() => {
    setSelected(new Set());
    setCursor(0);
    setProgramRef('');
  }, [merchantRef]);
  useEffect(() => {
    if (data.data) setMerchantName(data.data.merchant.name);
    if (data.data && !programRef) setProgramRef(data.data.merchant.activeProgramRef ?? data.data.programs[0]?.ref ?? '');
  }, [data.data, programRef]);

  const toggle = (ref: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  const openPreview = async (couponRefs?: string[]) => {
    const input = { merchantRef, programRef, ...(couponRefs ? { couponRefs } : {}) };
    setBusy(true);
    try {
      setPreview({ input, result: await api.op<RegeneratePreview>('admin.links.previewRegenerate', input) });
    } catch (error) {
      toast('error', describeError(error));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    const done = await runWrite<{ summary: RegeneratePreview['summary']; activated: number }>(
      toast,
      'admin.links.regenerate',
      preview.input,
      (r) => `Links stored: ${r.summary.new} new, ${r.summary.changed} changed${r.summary.fails ? `, ${r.summary.fails} failed` : ''}${r.activated ? `, ${r.activated} activated` : ''}`,
    );
    setBusy(false);
    if (done) {
      setPreview(null);
      setSelected(new Set());
    }
  };

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, coupons.length)),
      k: () => setCursor((c) => step(c, -1, coupons.length)),
      x: () => coupons[cursor] && toggle(coupons[cursor]!.ref),
    },
    preview === null,
  );

  const pick = (merchant: Named | null) => {
    setMerchantName(merchant?.name ?? null);
    setQuery({ merchant: merchant?.ref ?? null, site: null });
  };
  const activate = (link: LinkRow, kind: 'coupon' | 'merchant', label: string) =>
    runWrite(toast, 'admin.links.setActive', { kind, id: link.id }, `${label}: ${link.network.name} is now active${link.site ? ` on ${link.site.name}` : ''}`);
  const d = data.data;

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Links</h1>
          <p className="muted">Tracking links per coupon, network and site. One link is active per coupon and site.</p>
        </div>
      </header>
      <div className="aff-picker">
        <MerchantPicker value={merchantRef ? { ref: merchantRef, name: merchantName ?? merchantRef } : null} onChange={pick} />
        {d ? (
          <>
            <label>
              Site
              <select value={siteRef} onChange={(e) => setQuery({ site: e.target.value || null }, { replace: true })}>
                <option value="">All my sites</option>
                {d.sites.map((s) => (
                  <option key={s.ref} value={s.ref}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Program for regeneration
              <select value={programRef} onChange={(e) => setProgramRef(e.target.value)}>
                {d.programs.map((p) => (
                  <option key={p.ref} value={p.ref}>
                    {p.network.name} · {p.name}
                    {p.active ? ' (active)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {d.canChange ? (
              <div className="aff-inline">
                <button type="button" className="btn" disabled={busy || selected.size === 0 || !programRef} onClick={() => openPreview([...selected])}>
                  Regenerate selected ({selected.size})
                </button>
                <button type="button" className="btn" disabled={busy || !programRef} onClick={() => openPreview()}>
                  Regenerate all
                </button>
              </div>
            ) : null}
            <Link to={`/merchants/${d.merchant.ref}`}>Merchant page</Link>
          </>
        ) : null}
      </div>
      {!merchantRef ? <Empty>Pick a merchant to see its links.</Empty> : null}
      {data.error ? <ErrorBox error={data.error} onRetry={data.reload} /> : null}
      {merchantRef && !d && data.loading ? <Loading /> : null}
      {d ? (
        <>
          <p className="muted small">
            <Kbd>j</Kbd>/<Kbd>k</Kbd> move · <Kbd>x</Kbd> select for regeneration · links of other programs stay stored and can be made active with one click.
          </p>
          <table className="table">
            <thead>
              <tr>
                <th scope="col" aria-label="Select" />
                <th scope="col">Coupon</th>
                <th scope="col">Ends</th>
                <th scope="col">Links by site</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon, index) => (
                <tr key={coupon.ref} className={index === cursor ? 'cursor' : undefined} onMouseEnter={() => setCursor(index)}>
                  <td>
                    <input type="checkbox" checked={selected.has(coupon.ref)} onChange={() => toggle(coupon.ref)} aria-label={`Select ${coupon.title}`} />
                  </td>
                  <td>
                    <span className="strong">{coupon.title}</span> <small className="muted">{coupon.kind}</small>
                  </td>
                  <td>{formatDate(coupon.endsAt)}</td>
                  <td>
                    <LinkCells links={coupon.links} canChange={d.canChange} onActivate={(l) => activate(l, 'coupon', coupon.title)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <h2 className="section-title">Merchant links (store button and text links)</h2>
          {d.merchantLinks.length ? <LinkCells links={d.merchantLinks} canChange={d.canChange} onActivate={(l) => activate(l, 'merchant', d.merchant.name)} /> : <p className="muted">No merchant links yet.</p>}
        </>
      ) : null}
      {preview ? (
        <Modal
          title={`Regenerate on ${preview.result.network}`}
          onClose={() => setPreview(null)}
          actions={
            <>
              <button type="button" className="btn btn-primary" disabled={busy || !preview.result.allowed || preview.result.lines.every((l) => l.change === 'same' || l.change === 'fails')} onClick={confirm}>
                Store {preview.result.summary.new + preview.result.summary.changed} link(s)
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)}>
                Cancel
              </button>
            </>
          }
        >
          <ul className="summary-chips">
            <li>{preview.result.summary.new} new</li>
            <li>{preview.result.summary.changed} changed</li>
            <li>{preview.result.summary.same} unchanged</li>
            {preview.result.summary.fails ? <li className="warn">{preview.result.summary.fails} fail (current link kept)</li> : null}
          </ul>
          <p className="muted small">New links on a program that is not the merchant’s active one are stored but not activated. Nothing changes until you confirm.</p>
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            <table className="table">
              <tbody>
                {preview.result.lines.map((line, i) => (
                  <tr key={i}>
                    <td>{line.kind === 'merchant' ? <em>Merchant link</em> : line.couponTitle}</td>
                    <td>{line.site?.name}</td>
                    <td className={line.change === 'fails' ? 'text-warn' : undefined} title={line.error ?? line.newUrl ?? ''}>
                      {CHANGE_LABELS[line.change]}
                      {line.error ? `: ${line.error}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

function LinkCells({ links, canChange, onActivate }: { links: LinkRow[]; canChange: boolean; onActivate: (link: LinkRow) => void }) {
  if (links.length === 0) return <span className="text-warn">no link</span>;
  const bySite = new Map<string, LinkRow[]>();
  for (const link of links) bySite.set(link.site?.name ?? 'All sites', [...(bySite.get(link.site?.name ?? 'All sites') ?? []), link]);
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      {[...bySite.entries()].map(([site, rows]) => (
        <div key={site} className="aff-link-cell">
          <span className="muted" style={{ minWidth: 150 }}>
            {site}
          </span>
          {rows.map((link) => (
            <span key={link.id} className="aff-inline">
              <span className={`badge ${link.isActive ? 'badge-ok' : link.status === 'ok' ? 'badge-none' : 'badge-warn'}`} title={link.error ?? link.url ?? ''}>
                {link.isActive ? '● ' : ''}
                {link.network.name}
                {link.status !== 'ok' ? ` · ${link.status}` : ''}
              </span>
              {canChange && !link.isActive && link.status === 'ok' && link.url ? (
                <button type="button" className="btn btn-ghost btn-small" onClick={() => onActivate(link)} aria-label={`Make ${link.network.name} active on ${site}`}>
                  Make active
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
